import { isEmpty } from '@jjj/components';
import moment from 'moment';
import _cloneDeep from 'lodash/cloneDeep';
import actions from './actions';
import { createReducer, convertBaseErrors } from '../../../../../../helpers/reducer';
import { keyBy, keyByProperty } from '../../../../../../helpers/core';
import { withTZ } from '../../../../../../helpers/dateTime';
import { isDayforce } from '../../../../../../settings';
import { t } from '../../../../../../i18n';
import { isDayforceBenchMaofSpecific } from '../../../../../../settings/organizationSettings';

function timeChangedByMoreThan(start, finish, moreThan) {
  return Math.abs(start) > moreThan || Math.abs(finish) > moreThan;
}

function getShiftValue(item) {
  return keyByProperty(Object.values(item), 'name', prop => prop.value);
}

const initState = {
  initialForm: {},
  editForm: {},
  loading: false,
  isSaveLoading: false,
  formInfo: {},
  edited: false,
  step: 1,
  baseError: null,
  timeChanged: false,
  isNotificationSent: false,
  overtimeShifts: 0,
  isUpdateJobGroupsVmsIdLoading: false,
  isUpdateJobVmsIdLoading: false,
  isCancellationPoliciesCheckLoading: false,
  cancellation_policy: { may_apply: false },
  isUpdatePastJobLoading: false,
};

const initBookingData = {
  level: '',
  position: '',
  starts_at: null,
  finishes_at: null,
  workers_required: 1,
  default_shift_payouts: [],
  reset_priority_multiplier: false,
  keep_same_workers: true,
  latest_starts_at: null,
  shoesAttireItems: null,
  pantsAttireItems: null,
  shirtsAttireItems: null,
  recalculate_priority_multiplier: false,
  apply_payouts_changes_to_assigned_shifts: true,
  special_instructions: '',
  additional_tasks: '',
};

const initGigData = {
  level: '',
  position: '',
  starts_at: null,
  finishes_at: null,
  workers_required: 1,
  default_shift_payouts: [],
  reset_priority_multiplier: false,
  keep_same_workers: true,
  latest_starts_at: null,
  shoesAttireItems: null,
  pantsAttireItems: null,
  shirtsAttireItems: null,
  recalculate_priority_multiplier: false,
  special_instructions: '',
  additional_tasks: '',
};

function validate(attr, value, state, recordIndex) {
  let error = '';
  const finishesAtValue = attr === 'finishes_at' && value;
  const secondFinishesAtValue = state?.editForm?.starts_at?.value || null;
  const startsAtValue = attr === 'starts_at' && value;
  const secondStartsAtValue = state?.editForm?.finishes_at?.value || null;
  const defaultShiftPayouts = state?.editForm?.default_shift_payouts?.value || null;

  if (attr === 'starts_at') {
    if (isEmpty(startsAtValue)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.jobs.bookings.start_time') });
    }

    if (
      startsAtValue &&
      secondStartsAtValue &&
      withTZ(startsAtValue).diff(withTZ(secondStartsAtValue), 'minutes') >= 0
    ) {
      return t('dash.validation.mast_be_before_error', {
        firstFieldName: t('dash.jobs.bookings.start_date'),
        secondFieldName: t('dash.jobs.bookings.end_date'),
      });
    }
  }

  if (attr === 'finishes_at') {
    if (isEmpty(finishesAtValue)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.jobs.bookings.end_time') });
    }

    if (
      finishesAtValue &&
      secondFinishesAtValue &&
      withTZ(finishesAtValue).isSameOrBefore(withTZ(secondFinishesAtValue))
    ) {
      return t('dash.validation.mast_be_after_error', {
        firstFieldName: t('dash.jobs.bookings.end_date'),
        secondFieldName: t('dash.jobs.bookings.start_date'),
      });
    }
  }

  if (attr === 'payout_id') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.jobs.bookings.incentive_type_label') });
    }

    if (defaultShiftPayouts) {
      if (
        defaultShiftPayouts.some(
          item =>
            // eslint-disable-next-line no-underscore-dangle
            !item._destroy &&
            item.payout_id &&
            item.payout_id.value !== null &&
            item.payout_id.value === value &&
            item.organization?.id?.value &&
            defaultShiftPayouts[recordIndex] &&
            item.organization?.id?.value === defaultShiftPayouts[recordIndex].organization?.id?.value &&
            item.billable &&
            item.billable.value === defaultShiftPayouts[recordIndex].billable.value,
        )
      ) {
        error = t('dash.jobs.bookings.incentive_type_error');
      }
    }
  }

  return error;
}

function mapAttireItems(category, attireArray) {
  return attireArray.find(item => item.category.id === category);
}

function mapFormAttireItems(category, attireArray) {
  return attireArray.filter(item => item.category.id === category);
}

function getSortedAttireItems(attireArray) {
  return {
    shoesAttireItems: mapAttireItems('shoes', attireArray),
    pantsAttireItems: mapAttireItems('pants', attireArray),
    shirtsAttireItems: mapAttireItems('shirts', attireArray),
  };
}

function getSortedFormAttireItems(attireArray) {
  return {
    shoesAttireItems: mapFormAttireItems('shoes', attireArray),
    pantsAttireItems: mapFormAttireItems('pants', attireArray),
    shirtsAttireItems: mapFormAttireItems('shirts', attireArray),
  };
}

function formatDefaultShiftPayouts(payouts) {
  const getName = code => {
    if (code === 'BONUS') {
      return t('dash.jobs.bookings.incentive_types.bonus');
    }

    if (code === 'TRVL') {
      return t('dash.jobs.bookings.incentive_types.travel');
    }
  };

  return payouts.map(item => {
    return { ...item, name: getName(item.code) };
  });
}

function onLoad(state, { payload }) {
  const { data } = payload;
  const attireItems = data ? getSortedAttireItems(data.attire_items) : null;

  const editObject = data
    ? {
        ...initBookingData,
        recalculate_priority_multiplier: data.time_type !== 'past' && data.priority_multiplier > 1,
        ...data,
        ...attireItems,
      }
    : { ...initGigData };

  const propsArray = Object.keys(editObject);

  /* eslint-disable no-use-before-define */
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop =>
      prop === 'default_shift_payouts'
        ? getValue(prop, editObject[prop], state)
        : {
            name: prop,
            value:
              editObject[prop] && {}.propertyIsEnumerable.call(editObject[prop], 'id')
                ? editObject[prop].id
                : editObject[prop],
            error: validate(prop, editObject[prop], state),
            touched: false,
          },
  );
  /* eslint-enable no-use-before-define */

  return {
    ...state,
    loading: true,
    initialForm: _cloneDeep(editForm),
    editForm: _cloneDeep(editForm),
  };
}

function onClearStore() {
  return { ...initState };
}

function onClearStartsAtAndFinisheAtValues(state) {
  return {
    ...state,
    editForm: {
      ...state.editForm,
      starts_at: {
        name: 'starts_at',
        error: '',
        value: 'null',
        touched: false,
      },
      finishes_at: {
        name: 'finishes_at',
        error: '',
        value: 'null',
        touched: false,
      },
    },
  };
}

function onSaveRequest(state) {
  return {
    ...state,
    isSaveLoading: true,
  };
}

function onSaveSuccess(state) {
  return {
    ...state,
    edited: true,
    isSaveLoading: false,
    isNotificationSent: true,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;
  const baseError = convertBaseErrors(data);

  return {
    ...state,
    isSaveLoading: false,
    baseError,
  };
}

function onNext(state) {
  let { step } = state;

  // skip step 3 for SSA and future orgs
  if (!isDayforceBenchMaofSpecific() && step === 2) {
    step = 4;
  } else {
    step += 1;
  }

  const notifications = {};

  if (step === 4) {
    const { editForm, initialForm } = state;
    const startsAt = moment(editForm.starts_at.value);
    const finishesAt = moment(editForm.finishes_at.value);
    const initialStartsAt = moment(initialForm.starts_at.value);
    const initialFinishesAt = moment(initialForm.finishes_at.value);
    const startsTimeChanged = moment.duration(startsAt.diff(initialStartsAt)).asMinutes();
    const finishesTimeChanged = moment.duration(finishesAt.diff(initialFinishesAt)).asMinutes();
    const timeChangedMoreThan30Minutes = timeChangedByMoreThan(startsTimeChanged, finishesTimeChanged, 30);
    const sumAmounts = (prev, current) => prev + current.amount;

    const totalDurationIncreased =
      Math.abs(moment.duration(startsAt.diff(finishesAt)).asMinutes()) >
      Math.abs(moment.duration(initialStartsAt.diff(initialFinishesAt)).asMinutes());

    const workersRequiredChanged = editForm.workers_required.value !== initialForm.workers_required.value;

    const billableInviteIncentiveChanged =
      editForm.default_shift_payouts.value
        .map(getShiftValue)
        .filter(item => item.billable)
        .reduce(sumAmounts, 0) >
      initialForm.default_shift_payouts.value
        .map(getShiftValue)
        .filter(item => item.billable)
        .reduce(sumAmounts, 0);

    const notifyClientCondition =
      timeChangedMoreThan30Minutes ||
      totalDurationIncreased ||
      workersRequiredChanged ||
      billableInviteIncentiveChanged;

    if (notifyClientCondition) {
      notifications.notify_client = {
        name: 'notify_client',
        error: '',
        value: true,
        touched: true,
      };
    }

    const specialInstructionsChanged = editForm.special_instructions.value !== initialForm.special_instructions.value;
    const responsibilitiesChanged = editForm.additional_tasks.value !== initialForm.additional_tasks.value;
    const shoesAttireChanged = editForm.shoesAttireItems.value !== initialForm.shoesAttireItems.value;
    const shirtsAttireChanged = editForm.shirtsAttireItems.value !== initialForm.shirtsAttireItems.value;
    const pantsAttireChanged = editForm.pantsAttireItems.value !== initialForm.pantsAttireItems.value;
    const attireChanged = shoesAttireChanged || shirtsAttireChanged || pantsAttireChanged;

    const notifyAssignedTalentCondition =
      timeChangedMoreThan30Minutes || specialInstructionsChanged || responsibilitiesChanged || attireChanged;

    if (notifyAssignedTalentCondition) {
      notifications.notify_workers = {
        name: 'notify_workers',
        error: '',
        value: true,
        touched: true,
      };
    }

    // Date/time changed by more than the booking org's grace period (JJJ - 15 minutes, Bench - 5 minutes)
    const timeChanged = isDayforce()
      ? timeChangedByMoreThan(startsTimeChanged, finishesTimeChanged, 15)
      : timeChangedByMoreThan(startsTimeChanged, finishesTimeChanged, 5);

    // Invite incentive increased/added
    const inviteIncentiveChanged =
      editForm.default_shift_payouts.value.map(getShiftValue).reduce(sumAmounts, 0) >
      initialForm.default_shift_payouts.value.map(getShiftValue).reduce(sumAmounts, 0);

    const notifyNotAcceptedTalentCondition =
      timeChanged || specialInstructionsChanged || responsibilitiesChanged || attireChanged || inviteIncentiveChanged;

    if (notifyNotAcceptedTalentCondition) {
      notifications.notify_invitees = {
        name: 'notify_invitees',
        error: '',
        value: true,
        touched: true,
      };
    }
  }

  return { ...state, step, editForm: { ...state.editForm, ...notifications } };
}

function onPrev(state) {
  const { step } = state;

  // skip step 3 for SSA and future orgs
  if (!isDayforceBenchMaofSpecific()) {
    return {
      ...state,
      step: step === 4 ? 2 : step - 1,
    };
  }

  return {
    ...state,
    step: step - 1,
  };
}

function formatProp(payload, prop, state) {
  const getProp = () => ({
    name: prop,
    value: payload && payload[prop],
    error: payload && validate(prop, payload[prop], state),
    touched: false,
  });

  if (payload && Array.isArray(payload[prop])) {
    // eslint-disable-next-line no-use-before-define
    return formatArray(payload, prop, state);
  }

  if (payload && typeof payload[prop] === 'object' && payload[prop] != null) {
    // eslint-disable-next-line no-use-before-define
    return formatObject(payload, prop, state);
  }

  return getProp();
}

function formatArray(payload, prop, state) {
  return payload[prop].map((item, index) => formatProp(payload[prop], index, state));
}

function formatObject(payload, prop, state) {
  const propsArray = Object.keys(payload[prop]);

  return keyBy(
    propsArray,
    name => name,
    name => formatProp(payload[prop], name, state),
  );
}

export function getValue(attrs, value, state, recordIndex) {
  if (!Array.isArray(value) && typeof value === 'object' && value != null) {
    return keyBy(
      Object.keys(value),
      prop => prop,
      prop => formatProp(value, prop, state),
    );
  }

  return {
    name: attrs,
    touched: true,
    error: validate(attrs, value, state, recordIndex),
    value: Array.isArray(value) ? value.map((item, index) => formatProp(value, index, state)) : value,
  };
}

function onChangeNestedProperty(state, { payload }) {
  const { attrs, value } = payload;
  const editForm = { ...state.editForm };

  if (attrs.length > 2) {
    let data = null;
    const name = attrs[attrs.length - 1];
    for (let i = 0; i < attrs.length - 1; i += 1) {
      data = (data || editForm)[attrs[i]];
    }

    data[name] = getValue(name, value, state, attrs[attrs.length - 2]);
  } else if (attrs.length === 2) {
    if (editForm[attrs[0]] === undefined) {
      editForm[attrs[0]] = {};
    }
    editForm[attrs[0]][attrs[1]] = getValue(attrs, value, state, attrs[attrs.length - 2]);
  } else {
    editForm[attrs[0]] = getValue(attrs, value, state, attrs[attrs.length - 2]);
  }

  return { ...state, editForm };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;
  const timeChangedState = state.timeChanged;

  editForm[attr] = {
    value,
    error: validate(attr, value, state),
    name: attr,
    touched: true,
  };

  const timeChanged = attr === 'starts_at' || attr === 'finishes_at' ? true : timeChangedState;

  return {
    ...state,
    editForm: {
      ...editForm,
    },
    timeChanged,
  };
}

function onChangeProperties(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;
  const attributes = { [attr]: value };

  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop] && attributes[prop].id ? attributes[prop].id : attributes[prop],
      error: validate(prop, attributes[prop], state),
      touched: true,
    }),
  );

  return {
    ...state,
    editForm: {
      ...editForm,
      ...editFormProps,
    },
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormData(state, { payload }) {
  const { data, step } = payload;
  const attireItems = data.attire_items ? getSortedFormAttireItems(data.attire_items) : null;
  const payouts = data.payouts ? formatDefaultShiftPayouts(data.payouts) : [];

  return {
    ...state,
    step: step || 1,
    loading: false,
    formInfo: {
      ...attireItems,
      ...data,
      payouts,
    },
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initGigData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initGigData[prop],
      error: validate(prop, initGigData[prop], state),
      touched: false,
    }),
  );

  return {
    ...state,
    applicant: initGigData,
    editForm,
    isNotificationSent: true,
  };
}

function onCancel(state) {
  return {
    ...state,
    step: 1,
  };
}

function overTime(state, { payload }) {
  return {
    ...state,
    overtimeShifts: payload.overtime_shifts,
  };
}

function onAddNewIncentive(state, { payload }) {
  const { id, name, logo } = payload;
  const { editForm } = state;
  const { default_shift_payouts } = editForm;
  const { value } = default_shift_payouts;

  value.push({
    id: getValue('id', null, state),
    internal_id: getValue('internal_id', new Date().getTime(), state),
    amount: getValue('amount', 0, state),
    payout_id: getValue('payout_id', null, state),
    organization_id: getValue('organization_id', id, state),
    organization: {
      id: getValue('id', id, state),
      name: getValue('name', name, state),
      logo: getValue('logo', logo, state),
    },
    billable: getValue('billable', !isDayforce(), state),
  });

  editForm.default_shift_payouts = {
    ...state.editForm.default_shift_payouts,
    value,
  };

  return {
    ...state,
    editForm,
  };
}

function onDeleteIncentive(state, { payload }) {
  const { id, internal_id } = payload;

  const shiftPayoutsArray = state.editForm.default_shift_payouts.value;
  const findIndex = payout => {
    const index = shiftPayoutsArray.findIndex(item => {
      if (payout.id && payout.id.value) {
        return item.id.value === payout.id.value;
      }

      return item.internal_id && payout.internal_id && item.internal_id.value === payout.internal_id.value;
    });

    return index;
  };
  const index = findIndex({ id, internal_id });

  if (internal_id && internal_id.value) {
    shiftPayoutsArray.splice(index, 1);
  } else {
    shiftPayoutsArray[index] = {
      ...shiftPayoutsArray[index],
      _destroy: true,
    };
  }

  return {
    ...state,
    editForm: {
      ...state.editForm,
      default_shift_payouts: {
        ...state.editForm.default_shift_payouts,
        value: shiftPayoutsArray,
      },
    },
  };
}

function onUpdateJobGroupsVmsIdError(state) {
  return {
    ...state,
    isUpdateJobGroupsVmsIdLoading: false,
  };
}

function onUpdateJobGroupsVmsIdSuccess(state) {
  return {
    ...state,
    isUpdateJobGroupsVmsIdLoading: false,
  };
}

function onUpdateJobGroupsVmsIdRequest(state) {
  return {
    ...state,
    isUpdateJobGroupsVmsIdLoading: true,
  };
}

function onUpdateJobVmsIdError(state) {
  return {
    ...state,
    isUpdateJobVmsIdLoading: false,
  };
}

function onUpdateJobVmsIdRequest(state) {
  return {
    ...state,
    isUpdateJobVmsIdLoading: true,
  };
}

function onUpdateJobVmsIdSuccess(state) {
  return {
    ...state,
    isUpdateJobVmsIdLoading: false,
  };
}

function onCheckCancellationPoliciesRequest(state) {
  return {
    ...state,
    isCancellationPoliciesCheckLoading: true,
  };
}

function onCheckCancellationPoliciesSuccess(state, { payload }) {
  return {
    ...state,
    isCancellationPoliciesCheckLoading: false,
    cancellation_policy: { may_apply: payload.cancellation_policy_may_apply },
  };
}

function onUpdatePastJobRequest(state) {
  return {
    ...state,
    isUpdatePastJobLoading: true,
  };
}

function onUpdatePastJobSuccess(state) {
  return {
    ...state,
    isUpdatePastJobLoading: false,
  };
}

function onUpdatePastJobError(state) {
  return {
    ...state,
    isUpdatePastJobLoading: false,
  };
}

export { validate, getSortedAttireItems, getSortedFormAttireItems };
export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CLEAR_STARTS_AT_AND_FINISHES_AT_VALUES]: onClearStartsAtAndFinisheAtValues,
  [actions.CHANGE_NESTED_PROPERTY]: onChangeNestedProperty,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.SAVE]: onSaveRequest,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.NEXT]: onNext,
  [actions.PREV]: onPrev,
  [actions.TOUCH]: onTouch,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
  [actions.CREATE]: onCreate,
  [actions.CANCEL]: onCancel,
  [actions.OVERTIME_WARNING_SUCCESS]: overTime,
  [actions.ADD_NEW_INCENTIVE]: onAddNewIncentive,
  [actions.DELETE_INCENTIVE]: onDeleteIncentive,
  [actions.UPDATE_JOB_GROUPS_VMS_ID_ERROR]: onUpdateJobGroupsVmsIdError,
  [actions.UPDATE_JOB_GROUPS_VMS_ID_REQUEST]: onUpdateJobGroupsVmsIdRequest,
  [actions.UPDATE_JOB_GROUPS_VMS_ID_SUCCESS]: onUpdateJobGroupsVmsIdSuccess,
  [actions.UPDATE_JOB_VMS_ID_ERROR]: onUpdateJobVmsIdError,
  [actions.UPDATE_JOB_VMS_ID_REQUEST]: onUpdateJobVmsIdRequest,
  [actions.UPDATE_JOB_VMS_ID_SUCCESS]: onUpdateJobVmsIdSuccess,
  [actions.CHECK_CANCELLATION_POLICIES_REQUEST]: onCheckCancellationPoliciesRequest,
  [actions.CHECK_CANCELLATION_POLICIES_SUCCESS]: onCheckCancellationPoliciesSuccess,
  [actions.UPDATE_PAST_JOB_REQUEST]: onUpdatePastJobRequest,
  [actions.UPDATE_PAST_JOB_SUCCESS]: onUpdatePastJobSuccess,
  [actions.UPDATE_PAST_JOB_ERROR]: onUpdatePastJobError,
});
